import * as React from 'react';
import { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { createClientFileAsync } from '../ajax/requests';
import { loadEntities } from '../store/entities/actions';
import { isLoadingEntities } from '../store/entities/selectors';
import { Translate, useTranslations } from '../store/translations/hooks';
import { TK } from '../store/translations/translationKeys';
import { AutocompleteTextField, TextInputField } from './Forms/fields';
import { useReportsSettings } from '../store/settings/hooks';
import { DocumentSettings } from '../models';
import AppButton from './AppButton';

interface UploadFileModalProps {
    clientId: string
    show?: boolean
    close: () => void
    initialDocType?: string
}

export const genericFileSettings = (t: Translate<TK>): DocumentSettings => ({
    id: 'file',
    name: t(TK.file),
    availableForAppointmentsManager: true,
    availableForAllAppointmentTypes: true,
    availableForAppointmentTypes: [],
});

export const UploadFileModal: FC<UploadFileModalProps> = ({clientId, show, close, initialDocType}) => {
    const t = useTranslations()
    const dispatch = useDispatch()

    const {documents = []} = useReportsSettings()
    const [uploadFileSelected, setUploadFileSelected] = useState<undefined|{value: string, file?: File}>(undefined)
    const [uploadFileName, setUploadFileName] = useState<string>('')
    const [uploading, setUploading] = useState<boolean>(false)
    const [settings, setSettings] = useState<DocumentSettings>(documents.find(d => d.name === initialDocType) || genericFileSettings(t))

    useEffect(() => {
        if (show) setSettings(documents.find(d => d.name === initialDocType) || genericFileSettings(t));
    }, [show, documents, initialDocType])

    const loading = useSelector(isLoadingEntities('files'))

    const handleUploadFileChange = useCallback((value: string, file?: File) => {
        setUploadFileName(file?.name ?? '')
        setUploadFileSelected({value, file})
    }, [setUploadFileName, setUploadFileSelected])

    const handleUploadConfirmation = useCallback(() => {
        if(uploading || !uploadFileSelected?.file) return

        setUploading(true)

        const formData = new FormData();
        formData.append('file', uploadFileSelected.file, uploadFileName);

        formData.append('documentSettings.id', settings.id);
        formData.append('documentSettings.name', settings.name);
        settings.icon && formData.append('documentSettings.icon', settings.icon);
        settings.colorCode && formData.append('documentSettings.colorCode', settings.colorCode);
        if (settings.availableForAllAppointmentTypes != null) {
            formData.append('documentSettings.availableForAllAppointmentTypes', String(settings.availableForAllAppointmentTypes));
        }
        settings.availableForAppointmentTypes.forEach(type =>
            formData.append('documentSettings.availableForAppointmentTypes', type)
        );
        formData.append('documentSettings.availableForAppointmentsManager', String(settings.availableForAppointmentsManager));

        const requestTask = createClientFileAsync(clientId, formData)

        dispatch(loadEntities('files', () => requestTask.then(f => [f]), 'NewResult'))

        requestTask
            .then(() => {
                close()
                setUploadFileSelected(undefined)
            })
            .finally(() => setUploading(false))
    }, [uploading, uploadFileSelected, uploadFileName, clientId, dispatch, close, settings, documents, setUploadFileSelected, setUploading])
    
    return (
        <Modal size="lg" isOpen={show} toggle={close}>
            <ModalHeader toggle={close}>{t(TK.uploadFile)}</ModalHeader>
            <ModalBody>
                <TextInputField 
                    type='file'
                    value={uploadFileSelected?.value}
                    onChange={handleUploadFileChange}
                />
                {uploadFileSelected && 
                    <>
                        <hr/>
                        <Form style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
                            <TextInputField 
                                label={t(TK.name)}
                                value={uploadFileName}
                                onChange={setUploadFileName}
                            />
                            <AutocompleteTextField 
                                label={t(TK.type)}
                                options={[t(TK.file), ...documents.map(d => d.name)]}
                                value={settings.name}
                                onValueChanged={type => setSettings(documents.find(d => d.name === type) || genericFileSettings(t))}
                            />
                        </Form>
                        <ModalFooter>
                            <AppButton disabled={loading || uploading} onClick={handleUploadConfirmation} color="success">{t(TK.uploadFile)}</AppButton>
                        </ModalFooter>
                    </>
                }
            </ModalBody>
        </Modal> 
    )
}